/**
 * GSGO wordmark. `placeholder` = dashed “image placeholder” frame with label text (header/footer).
 * `colored` / `white` intentionally render placeholder style to avoid legacy raster lockups.
 * Default `text` = plain wordmark.
 */

interface BrandLogoProps {
  className?: string;
  alt?: string;
  /**
   * `placeholder` = bordered box spelling the label (e.g. GSGO). `white` / `colored` = raster lockups.
   * `text` = plain wordmark.
   */
  variant?: 'text' | 'white' | 'colored' | 'placeholder';
  /** Override the word “GSGO” (e.g. from branding settings). */
  label?: string;
  /** Renders a short line under the wordmark (e.g. tagline). */
  tagline?: string;
  /** If false, hide tagline even when `tagline` is set. */
  showTagline?: boolean;
  /** For `variant="text"` or `placeholder`: light text / light placeholder on dark surfaces. */
  onDark?: boolean;
}

const MISSION_BLUE = '#1A5276';

export const BrandLogo = ({
  className = '',
  alt = 'GSGO',
  variant = 'text',
  label = 'GSGO',
  tagline,
  showTagline = false,
  onDark = false,
}: BrandLogoProps): JSX.Element => {
  if (variant === 'placeholder') {
    return (
      <span
        role="img"
        aria-label={alt}
        className={`inline-flex items-center justify-center rounded-lg border-2 border-dashed px-3 py-1.5 text-base sm:text-lg font-extrabold tracking-tight tabular-nums ${
          onDark
            ? 'border-white/35 bg-white/[0.07] text-white'
            : 'border-slate-300 bg-slate-100 text-[#1A5276]'
        } ${className}`}
      >
        {label}
      </span>
    );
  }
  if (variant === 'colored' || variant === 'white') {
    return (
      <span
        role="img"
        aria-label={alt}
        className={`inline-flex items-center justify-center rounded-lg border-2 border-dashed px-3 py-1.5 text-base sm:text-lg font-extrabold tracking-tight tabular-nums ${
          variant === 'white' || onDark
            ? 'border-white/35 bg-white/[0.07] text-white'
            : 'border-slate-300 bg-slate-100 text-[#1A5276]'
        } ${className}`}
      >
        {label}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex flex-col items-start min-w-0 ${className}`}
      style={{ lineHeight: 1.1 }}
    >
      <span
        className="text-xl sm:text-2xl font-extrabold tracking-tight"
        style={{
          color: onDark ? '#ffffff' : MISSION_BLUE,
          fontFamily: '"Open Sauce Sans", "Montserrat", system-ui, sans-serif',
        }}
      >
        {label}
      </span>
      {showTagline && tagline ? (
        <span
          className="mt-0.5 text-[0.65rem] sm:text-xs font-medium max-w-[14rem] leading-snug"
          style={{ color: onDark ? 'rgba(255,255,255,0.88)' : '#64748b' }}
        >
          {tagline}
        </span>
      ) : null}
    </span>
  );
};
